import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../contexts/GameContext';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';
import Modal from './Modal';
import './HomePage.css';

const HomePage = () => {
  const navigate = useNavigate();
  const { state, actions } = useGame();
  const [playerName, setPlayerName] = useState('');
  const [showJoinRoomModal, setShowJoinRoomModal] = useState(false);
  const [roomId, setRoomId] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [password, setPassword] = useState('');

  // 컴포넌트 마운트 시 로컬 스토리지에서 플레이어 이름 불러오기
  useEffect(() => {
    const savedPlayerName = localStorage.getItem('playerName');
    if (savedPlayerName) {
      setPlayerName(savedPlayerName);
    }
  }, []);

  // 플레이어 이름이 변경될 때마다 로컬 스토리지에 저장
  useEffect(() => {
    if (playerName.trim()) {
      localStorage.setItem('playerName', playerName.trim());
    }
  }, [playerName]);

  // 방 생성/참여 성공 시 로비로 이동
  useEffect(() => {
    if (state.room.id && state.ui.isConnected) {
      navigate('/lobby');
    }
  }, [state.room.id, state.ui.isConnected, navigate]);

  // 플레이어 이름 유효성 검사
  const validatePlayerName = () => {
    if (!playerName.trim()) {
      setFormErrors({ playerName: '플레이어 이름을 입력해주세요.' });
      return false;
    }
    if (playerName.trim().length < 2) {
      setFormErrors({ playerName: '플레이어 이름은 최소 2자 이상이어야 합니다.' });
      return false;
    }
    if (playerName.trim().length > 12) {
      setFormErrors({ playerName: '플레이어 이름은 최대 12자까지 가능합니다.' });
      return false;
    }
    setFormErrors({});
    return true;
  };

  // 방 ID 유효성 검사
  const validateRoomId = () => {
    if (!roomId.trim()) {
      setFormErrors({ roomId: '방 ID를 입력해주세요.' });
      return false;
    }
    setFormErrors({});
    return true;
  };

  // 방 참여 모달 열기
  const openJoinRoomModal = () => {
    if (!validatePlayerName()) return;
    setShowJoinRoomModal(true);
  };

  // 방 참여 처리
  const handleJoinRoom = () => {
    if (!validateRoomId()) return;

    actions.setPlayer({
      name: playerName.trim(),
      isHost: false
    });

    actions.joinRoom(roomId.trim(), {
      name: playerName.trim(),
      password: password || ''
    });
  };

  // 에러 메시지 닫기
  const closeError = () => {
    actions.clearError();
  };

  // 모달 닫기
  const closeModals = () => {
    setShowJoinRoomModal(false);
    setRoomId('');
    setPassword('');
    setFormErrors({});
  };

  return (
    <div className="homepage-container">
      <div className="homepage-overlay"></div>
      <img src="/resource/home/title.png" alt="Death by Clevi" className="game-title" />
      <div className="homepage-content">
        <p className="game-description">클레비와 함께하는 AI 생존게임</p>
        
        {/* 연결 상태 표시 */}
        <div className="connection-status" style={{paddingBottom :"50px"}}>
          {state.ui.isConnected ? (
            <span className="connected">서버에 연결됨</span>
          ) : (
            <span className="disconnected">서버 연결 중...</span>
          )}
        </div>
        
        <div className="player-name-form" >
          <label style={{ fontSize: "24px" ,paddingBottom :"20px" }} htmlFor="playerName">플레이어 이름을 적어주세요</label>
          <input
            type="text"
            id="playerName"
            value={playerName}
            onChange={(e) => setPlayerName(e.target.value)}
            placeholder="이름을 입력하세요"
            maxLength={12}
            disabled={!state.ui.isConnected}
            autoComplete="off"
            style={{ marginBottom: "200px" }}
          />
          {formErrors.playerName && (
            <div className="error-text">{formErrors.playerName}</div>
          )}
        </div>

        <div className="action-buttons" >
          <button
            className="join-game-btn"
            onClick={() => {
              // 플레이어 이름을 설정하고 로비로 이동
              actions.setPlayer({ name: playerName.trim() });
              navigate('/lobby');
            }}
            disabled={!playerName.trim() || !state.ui.isConnected}
            aria-label="로비로 이동"
          >
          </button>
        </div>

        {state.ui.error && (
          <ErrorMessage
            message={state.ui.error}
            onClose={closeError}
          />
        )}
      </div>

      {/* 방 참여 모달 */}
      <Modal
        isOpen={showJoinRoomModal}
        onClose={closeModals}
        title="방 참여하기"
        size="small"
      >
        <div className="modal-form">
          <div className="form-group">
            <label htmlFor="roomId">방 ID</label>
            <input
              type="text"
              id="roomId"
              value={roomId}
              onChange={(e) => setRoomId(e.target.value)}
              placeholder="방 ID를 입력하세요"
            />
            {formErrors.roomId && (
              <div className="error-text">{formErrors.roomId}</div>
            )}
          </div>
          
          <div className="form-group">
            <label htmlFor="joinPassword">비밀번호 (필요시)</label>
            <input
              type="password"
              id="joinPassword"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="비밀번호를 입력하세요"
            />
          </div>
          
          <div className="modal-actions">
            <button className="cancel-btn" onClick={closeModals}>
              취소
            </button>
            <button
              className="confirm-btn"
              onClick={handleJoinRoom}
              disabled={state.ui.isLoading}
            >
              {state.ui.isLoading ? <LoadingSpinner size="small" /> : '참여'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default HomePage;
